"use client";

import React from "react";
import { Phone, Shield, Clock, Star, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function CompanionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default text-foreground">Inspection Scheduled</h3>
        <p className="text-muted-foreground max-w-md font-serif">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-primary/50 hover:bg-primary/10 rounded-lg font-default">
          Request Another Quote
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="full_name" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-bold text-foreground">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Your name" {...field} className="font-default" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-bold text-foreground">Email Address</FormLabel>
            <FormControl>
              <Input placeholder="your@email.com" type="email" {...field} className="font-default" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-bold text-foreground">Phone Number</FormLabel>
            <FormControl>
              <Input placeholder="(941) 555-0123" type="tel" {...field} className="font-default" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="occasion" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-bold text-foreground">Type of Service Needed</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="font-default">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="general_pest">General Pest Control</SelectItem>
                <SelectItem value="termite">Termite Treatment</SelectItem>
                <SelectItem value="mosquito">Mosquito Control</SelectItem>
                <SelectItem value="rodent">Rodent Control</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="event_date" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-bold text-foreground">Preferred Inspection Date</FormLabel>
            <FormControl>
              <Input type="date" {...field} className="font-default" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="duration_hours" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-bold text-foreground">Property Size (sq ft)</FormLabel>
            <FormControl>
              <Input placeholder="e.g., 2000" type="number" {...field} className="font-default" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-bold text-foreground">Additional Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your pest control concerns..." className="font-default resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <Button type="submit" disabled={isSubmitting} className="w-full font-default font-bold uppercase tracking-wider bg-primary hover:bg-primary/90 text-primary-foreground">
        {isSubmitting && <Loader className="mr-2 size-4 animate-spin" />}
        {isSubmitting ? "Sending..." : "Send Request"}
      </Button>

      {submitError && <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/30">
          <CircleAlert className="size-5 text-destructive mt-0.5 flex-shrink-0" />
          <p className="text-sm text-destructive font-default">{submitError}</p>
        </div>}
    </Form>;
}
export default function SecureYourCompanion() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="241608" id="secure-your-inspection" className="bg-background text-foreground py-24 lg:py-32 relative overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-lg bg-primary/10 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-lg bg-primary/10 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {}
          <div className={`lg:col-span-5 space-y-8 transition-all duration-1000 ease-out ${isSectionVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary text-primary-foreground text-sm font-bold tracking-widest uppercase font-default">
                <Star className="size-4 fill-primary-foreground" />
                <span>Local Experts</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-default font-bold leading-tight text-foreground uppercase tracking-tighter">
                Secure Your <br />
                <span className="text-foreground">
                  Free Inspection
                </span>
              </h2>
              <p className="text-2xl font-serif text-muted-foreground max-w-md leading-relaxed">
                Protect your home with Punta Gorda's most trusted pest control team. Fast, reliable, and professional service guaranteed.
              </p>
            </div>

            <div className="relative overflow-hidden border-2 border-primary group">
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 via-transparent to-transparent z-10" />
              <img src="https://images.unsplash.com/photo-1558905619-171426efb452?q=80&w=2000&auto=format&fit=crop" data-wvc-srcset="https://images.unsplash.com/photo-1558905619-171426efb452?q=80&w=2000&auto=format&fit=crop-wvc-srcset" data-wvc-sizes="https://images.unsplash.com/photo-1558905619-171426efb452?q=80&w=2000&auto=format&fit=crop-wvc-sizes" alt="Well-maintained Florida residential lawn" className="w-full h-[300px] object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              
              <div className="absolute bottom-0 left-0 w-full p-6 z-20">
                <div className="bg-card border-2 border-primary p-5 flex items-center gap-4">
                  <div className="size-12 bg-primary flex items-center justify-center shrink-0">
                    <Phone className="size-5 text-primary-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground font-bold font-default uppercase tracking-wider mb-1">Call Now</p>
                    <Link to="tel:+19415550123" className="text-2xl font-default font-bold text-foreground hover:text-primary transition-colors">(941) 888-0485</Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="flex items-center gap-3">
                <Clock className="size-5 text-primary" />
                <span className="text-sm font-default font-bold uppercase tracking-wide text-foreground">Same-Day Service</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="size-5 text-primary" />
                <span className="text-sm font-default font-bold uppercase tracking-wide text-foreground">Licensed & Insured</span>
              </div>
            </div>
          </div>

          {}
          <div ref={formRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${isFormVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="bg-card border-2 border-primary p-6 sm:p-10 relative">
              {}
              <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-primary pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-primary pointer-events-none" />
              
              <div className="mb-8">
                <h3 className="text-3xl font-default font-bold text-foreground mb-2 uppercase tracking-tight">Request a Quote</h3>
                <p className="text-lg font-serif text-muted-foreground">Provide your details below and our local experts will contact you within 24 hours.</p>
              </div>

              <FormProvider formKey="free_inspection_form_home" formId="free_inspection_form_home" sectionName="Secure Your Inspection" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              occasion: "",
              event_date: "",
              duration_hours: "",
              additional_details: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              occasion: {
                required: true
              },
              event_date: {
                required: true
              },
              duration_hours: {
                required: true,
                min: 1,
                max: 10000
              }
            }} submitText="Send Request" successMessage="Your request has been received. A Peace River Pest Control specialist will contact you shortly." errorMessage="There was an error submitting your request. Please call us directly at (941) 555-0123.">
                <CompanionForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}